import type { LineItem, Order, PaymentStatus, PaymentEntry } from '../types';

export function calcLineTotal(qty: number, rate: number): number {
  return Math.round(qty * rate * 100) / 100;
}

export function calcOrderTotal(items: LineItem[]): number {
  return items.reduce((sum, i) => sum + i.total, 0);
}

export function calcPaymentStatus(
  orderTotal: number,
  payments: PaymentEntry[],
  advanceAmount: number
): PaymentStatus {
  const paid =
    advanceAmount + payments.reduce((s, p) => s + p.amount, 0);
  if (paid <= 0) return 'unpaid';
  if (paid >= orderTotal - 0.01) return 'paid';
  return 'partial';
}

export function calcBalanceDue(
  orderTotal: number,
  payments: PaymentEntry[],
  advanceAmount: number
): number {
  const paid =
    advanceAmount + payments.reduce((s, p) => s + p.amount, 0);
  return Math.max(0, Math.round((orderTotal - paid) * 100) / 100);
}

export function isDueSoon(dateStr: string): boolean {
  const due = new Date(dateStr);
  const now = new Date();
  const diff = due.getTime() - now.getTime();
  return diff >= 0 && diff <= 24 * 60 * 60 * 1000;
}

export function isOverdue(dateStr: string, stage: string): boolean {
  if (stage === 'delivered' || stage === 'cancelled') return false;
  const due = new Date(dateStr);
  due.setHours(23, 59, 59, 999);
  return due < new Date();
}

export function canCloseOrder(order: Order): boolean {
  return (
    order.stage !== 'cancelled' &&
    (order.qc?.outcome === 'passed' || order.stage === 'ready_for_dispatch' || order.stage === 'dispatched' || order.stage === 'delivered') &&
    (order.dispatch?.status === 'delivered' || order.stage === 'delivered') &&
    order.paymentStatus === 'paid'
  );
}

export function generateOrderId(existing: Order[]): string {
  const year = new Date().getFullYear();
  const prefix = `UA-${year}-`;
  const nums = existing
    .map((o) => o.orderId)
    .filter((id) => id.startsWith(prefix))
    .map((id) => parseInt(id.replace(prefix, ''), 10))
    .filter((n) => !isNaN(n));
  const next = (nums.length ? Math.max(...nums) : 0) + 1;
  return `${prefix}${String(next).padStart(4, '0')}`;
}

export function uid(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}
